import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Assignment,
  Timer,
  CalendarToday,
  Work,
  Code,
  Description,
  Quiz,
  Upload,
  Link as LinkIcon,
  Assessment,
  Visibility,
  Group,
  CheckCircle,
  HourglassEmpty
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Tasks = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isRecruiter } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchTasks();
  }, [searchTerm, filterCategory, filterDifficulty, filterType]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterCategory) params.append('category', filterCategory);
      if (filterDifficulty) params.append('difficulty', filterDifficulty);
      if (filterType) params.append('taskType', filterType);
      
      const response = await axios.get(
        `http://localhost:5000/api/tasks?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setTasks(response.data.data || []);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `http://localhost:5000/api/tasks/${taskToDelete._id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setDeleteDialog(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
    }
  };

  const getTaskTypeIcon = (type) => {
    const icons = {
      'multiple-choice': <Quiz />,
      'essay': <Description />,
      'code': <Code />,
      'file-upload': <Upload />,
      'url-submission': <LinkIcon />,
      'project': <Work />
    };
    return icons[type] || <Assignment />;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'easy': 'success',
      'medium': 'warning',
      'hard': 'error',
      'expert': 'secondary'
    };
    return colors[difficulty] || 'default';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'coding': 'primary',
      'design': 'secondary',
      'writing': 'info',
      'analytical': 'warning',
      'behavioral': 'success',
      'case-study': 'error',
      'presentation': 'default',
      'other': 'default'
    };
    return colors[category] || 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (days) => {
    if (days === 1) return '1 day';
    if (days === 7) return '1 week';
    if (days === 14) return '2 weeks';
    if (days === 21) return '3 weeks';
    if (days === 30) return '1 month';
    return `${days} days`;
  };

  const activeTasks = tasks.filter(task => task.isActive);
  const inactiveTasks = tasks.filter(task => !task.isActive);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          Task Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/create-task')}
        >
          Create Task
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="coding">Coding</MenuItem>
                <MenuItem value="design">Design</MenuItem>
                <MenuItem value="writing">Writing</MenuItem>
                <MenuItem value="analytical">Analytical</MenuItem>
                <MenuItem value="behavioral">Behavioral</MenuItem>
                <MenuItem value="case-study">Case Study</MenuItem>
                <MenuItem value="presentation">Presentation</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={filterDifficulty}
                onChange={(e) => setFilterDifficulty(e.target.value)}
                label="Difficulty"
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
                <MenuItem value="expert">Expert</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Task Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Task Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                <MenuItem value="essay">Essay/Writing</MenuItem>
                <MenuItem value="code">Coding</MenuItem>
                <MenuItem value="file-upload">File Upload</MenuItem>
                <MenuItem value="url-submission">URL Submission</MenuItem>
                <MenuItem value="project">Project</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs for Active/Inactive Tasks */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={activeTasks.length} color="primary">
                Active Tasks
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={inactiveTasks.length} color="default">
                Inactive Tasks
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Task Cards */}
      <Grid container spacing={3}>
        {(tabValue === 0 ? activeTasks : inactiveTasks).map((task) => (
          <Grid item xs={12} md={6} lg={4} key={task._id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Tooltip title={task.taskType}>
                    {getTaskTypeIcon(task.taskType)}
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={task.category}
                      size="small"
                      color={getCategoryColor(task.category)}
                    />
                    <Chip
                      label={task.difficulty}
                      size="small"
                      color={getDifficultyColor(task.difficulty)}
                    />
                  </Box>
                </Box>
                
                <Typography variant="h6" gutterBottom>
                  {task.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {task.description?.substring(0, 100)}
                  {task.description?.length > 100 && '...'}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timer fontSize="small" color="action" />
                    <Typography variant="body2">
                      Time Limit: {formatTime(task.timeLimit)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" color="action" />
                    <Typography variant="body2">
                      Deadline: {formatDate(task.deadline)}
                    </Typography>
                  </Box>
                  
                  {task.mappedJobs && task.mappedJobs.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Work fontSize="small" color="action" />
                      <Typography variant="body2">
                        Mapped to {task.mappedJobs.length} job{task.mappedJobs.length > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}
                  
                  {task.statistics && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assessment fontSize="small" color="action" />
                      <Typography variant="body2">
                        {task.statistics.completions} completions
                        {task.statistics.passRate > 0 && ` (${task.statistics.passRate.toFixed(0)}% pass rate)`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
              
              <CardActions>
                <Tooltip title="View Submissions">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/task-submissions/${task._id}`)}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Edit Task">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/edit-task/${task._id}`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Delete Task">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTaskToDelete(task);
                      setDeleteDialog(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
                
                <Box sx={{ flexGrow: 1 }} />
                
                {task.isActive ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Active"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<HourglassEmpty />}
                    label="Inactive"
                    color="default"
                    size="small"
                  />
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {tasks.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No tasks found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create your first task to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/create-task')}
            sx={{ mt: 2 }}
          >
            Create Task
          </Button>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{taskToDelete?.title}"?
            {taskToDelete?.statistics?.completions > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This task has {taskToDelete.statistics.completions} submissions.
                It will be deactivated instead of deleted.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteTask} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Tasks;
